import type { ExtractionListItemDto } from '@sara/api-client';
import { Badge } from '@sara/ui/components/badge';
import { DataPanel } from '@sara/ui/components/data-panel';
import { Link, type LinkProps } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';

interface ExtractionListProps {
  extractions: ExtractionListItemDto[];
  getLinkProps: (extraction: ExtractionListItemDto) => LinkProps;
}

interface RowProps {
  extraction: ExtractionListItemDto;
  linkProps: LinkProps;
}

function Row({ extraction, linkProps }: RowProps) {
  return (
    <Link
      {...linkProps}
      className="group grid grid-cols-[1fr_auto] items-center gap-6 px-6 py-5 transition-colors hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background"
    >
      <div className="min-w-0">
        <div className="flex items-baseline gap-3">
          <p className="text-[15px] font-medium tracking-tight text-foreground transition-colors group-hover:text-primary">
            {extraction.name}
          </p>
          <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
            v{extraction.latestVersion}
          </span>
          {extraction.archivedAt ? (
            <Badge variant="outline" className="text-[10px]">
              Archived
            </Badge>
          ) : null}
        </div>
        {extraction.description && (
          <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">
            {extraction.description}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-6 text-xs text-muted-foreground">
        <span className="tabular-nums">
          {new Date(extraction.updatedAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
        <ArrowUpRight
          className="h-4 w-4 -translate-x-1 text-muted-foreground/60 transition-all group-hover:translate-x-0 group-hover:text-foreground"
          strokeWidth={1.5}
        />
      </div>
    </Link>
  );
}

export function ExtractionList({ extractions, getLinkProps }: ExtractionListProps) {
  if (extractions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/80 bg-card/40 px-6 py-20 text-center">
        <p className="font-display text-xl font-medium text-foreground">No extractions yet.</p>
        <p className="mt-2 max-w-xs text-sm text-muted-foreground">
          Create your first extraction to pull structured data from conversations.
        </p>
      </div>
    );
  }

  return (
    <DataPanel>
      <DataPanel.Header columns="1fr auto">
        <span>Name</span>
        <span>Updated</span>
      </DataPanel.Header>
      <DataPanel.Body>
        <ul className="divide-y divide-border/60">
          {extractions.map((extraction) => (
            <li key={extraction.id}>
              <Row extraction={extraction} linkProps={getLinkProps(extraction)} />
            </li>
          ))}
        </ul>
      </DataPanel.Body>
      <DataPanel.Footer>
        <span className="tabular-nums">
          {extractions.length} {extractions.length === 1 ? 'extraction' : 'extractions'}
        </span>
      </DataPanel.Footer>
    </DataPanel>
  );
}
